// The ruins (look.json `ruin`, docs/164 "The ruins' look", docs/166 C2): ported from `ruin_walls`,
// `ruin_ground`, `ruin_dissolve`, `ruin_field` and `ruin_tones` in `tools/encounter-art.py`, with each
// biome's own rules from its `ruins_board.py.txt` (the lone stub, the wet line, the ash). Fronts lose whole
// blocks from the top, heads and paving lose whole stones toward open ground, moss goes stone by stone,
// and a wall no open square touches goes back to the country. Every colour the painter adds is a pixel
// of the set's own `ruin_tones` file, so seasons and nights remap them with the art.
import type { Piece, Standing } from '../types';
import { Canvas } from './canvas';
import { C as CELL, type Ctx } from './context';
import type { Ground } from './ground';
import { tallFront, type WallsOut } from './walls';
import { ALPHA, at, fieldOf, hasher, maskRuns, middle, oneOf, words } from './places-px';

// module-local copies: the hot loops read them per pixel
const C = CELL, AM = ALPHA;

interface Ruin { levels: number[]; end_fall: number; door_rows: number; ivy_every: number }

export const isRuin = (ctx: Ctx): boolean => !!ctx.look.ruin && ctx.has('ruin_tones');
const ruinOf = (ctx: Ctx): Ruin => ctx.look.ruin as Ruin;

interface Tones {
  capLit: number; capBody: number; line: number; headLine: number;
  face: number[]; moss: number[]; green: number[]; earth: number[]; joints: Set<number>;
  headJoints: Set<number>; tuft: number[]; blade: number[];
  /** Columns 20 and 21: the wet line under a stone in water and the pixel below it (docs/166 C2, the wetland ruins). */
  wet: number; wetSoft: number;
  /** Columns 14 and 15 (the earth): the rust stain above the ashlands' spring. */
  stain: [number, number];
}

function tonesOf(ctx: Ctx): Tones {
  const p = ctx.all('ruin_tones')[0]!;
  const A = (i: number): number => at(p, i, 0), B = (i: number): number => at(p, i, 12);
  const r = (f: (i: number) => number, a: number, b: number): number[] => Array.from({ length: b - a }, (_, k) => f(a + k));
  return {
    capLit: A(0), capBody: A(1), line: A(2), headLine: A(3), face: r(A, 4, 7), moss: r(A, 7, 11), green: r(A, 11, 14), earth: r(A, 14, 16),
    joints: new Set(r(A, 16, 20)), headJoints: new Set(r(B, 0, 3)), tuft: r(B, 3, 8), blade: r(B, 9, 12),
    wet: A(20), wetSoft: A(21), stain: [A(14), A(15)],
  };
}

/** The ruins' biomes whose boards take a lone head stone off (`dissolve` in their `ruins_board.py.txt`, docs/166 C2). */
const LONE_STUB = new Set(['desert', 'highland', 'jungle', 'underdark', 'ashlands']);

/** What a square is to the ruins (the tool's `wall`, `door`, `front_at`, `deep`). */
class Plan {
  readonly w: number;
  readonly h: number;
  private readonly wallA: Uint8Array;
  private readonly doorA: Uint8Array;
  private readonly deepA: Uint8Array;
  constructor(readonly ctx: Ctx) {
    const { sq } = ctx;
    this.w = sq.w; this.h = sq.h;
    const n = sq.w * sq.h;
    this.wallA = new Uint8Array(n); this.doorA = new Uint8Array(n); this.deepA = new Uint8Array(n);
    for (let y = 0; y < sq.h; y++) for (let x = 0; x < sq.w; x++) {
      const s = sq.sq[y * sq.w + x];
      // a door in a wall that runs across the board is raised with its wall; a side door lies flat (index.ts)
      const door = (s === 'door' || s === 'way') && !(sq.solid(x, y - 1) && sq.solid(x, y + 1)) && (sq.solid(x, y - 1) || sq.isWall(x - 1, y) || sq.isWall(x + 1, y));
      this.doorA[y * sq.w + x] = door ? 1 : 0;
      // a lone wall in a room is a pillar, stood by the painter from `stand` (index.ts): not a wall here
      const pillar = s === 'wall' && ctx.has('stand') && !sq.isWall(x - 1, y) && !sq.isWall(x + 1, y) && !sq.isWall(x, y - 1) && !sq.isWall(x, y + 1);
      this.wallA[y * sq.w + x] = door || (s === 'wall' && !pillar) || s === 'rock' ? 1 : 0;
    }
    for (let y = 0; y < sq.h; y++) for (let x = 0; x < sq.w; x++) {
      if (!this.wall(x, y)) continue;
      let all = true;
      for (let j = -1; j <= 1 && all; j++) for (let i = -1; i <= 1; i++) {
        const a = x + i, b = y + j;
        if (sq.on(a, b) && !this.wall(a, b) && sq.at(a, b) !== 'void') { all = false; break; }
      }
      this.deepA[y * sq.w + x] = all ? 1 : 0;
    }
  }
  on(x: number, y: number): boolean { return x >= 0 && y >= 0 && x < this.w && y < this.h; }
  wall(x: number, y: number): boolean { return this.on(x, y) && this.wallA[y * this.w + x] === 1; }
  door(x: number, y: number): boolean { return this.on(x, y) && this.doorA[y * this.w + x] === 1; }
  front(x: number, y: number): boolean { return this.wall(x, y) && !this.wall(x, y + 1); }
  /** A wall no open square touches: gone back to the country. */
  deep(x: number, y: number): boolean { return this.on(x, y) && this.deepA[y * this.w + x] === 1; }
  liquid(x: number, y: number): boolean { const s = this.ctx.sq.at(x, y); return s === 'water' || s === 'deep'; }
  /** The tool's `soft`: the kit's own grass (a deep wall), the void, or water; paving thins out toward it. */
  soft(x: number, y: number): boolean { return !this.on(x, y) || this.ctx.sq.at(x, y) === 'void' || this.liquid(x, y) || this.deep(x, y); }
}

const plans = new WeakMap<Ctx, Plan>();
const planOf = (ctx: Ctx): Plan => { let p = plans.get(ctx); if (!p) { p = new Plan(ctx); plans.set(ctx, p); } return p; };

/** Every open square's ground is the ruins' own long grass, flipped but never turned; the paving is laid over it after (`ruinGround`). */
export function ruinGroundOf(ctx: Ctx, x: number, y: number, s: string, g: Ground | undefined): Ground | undefined {
  if (s === 'water' || s === 'deep' || s === 'void') return g;
  // rough borrowed from the country (EA14) stays rough: the rules read it so
  if (g && g.name !== 'floor' && (g.name !== 'rough' || ctx.borrowed.has('rough')) && g.name !== 'floor_patch') return g;
  return grassAt(ctx, x, y) ?? g;
}

function grassAt(ctx: Ctx, x: number, y: number): Ground | undefined {
  const vs = ctx.all('floor_grass');
  if (!vs.length) return undefined;
  const r = ctx.roll('g:floor_grass', x, y), p = vs[r % vs.length]!, flip = Math.floor(r / vs.length) % 2 === 1;
  return { name: 'floor', piece: p, ...(flip ? { turn: { flipX: true } } : {}), key: `${p.frame}/${flip ? 1 : 0}` };
}

/**
 * After the grounds: the deep's soft pool, grass under the walls, the paving (`ruin_ground`) and the wet
 * line where water meets a kept stone or the grass.
 */
export function ruinAfterGround(ctx: Ctx, out: Canvas): void {
  const P = planOf(ctx), T = tonesOf(ctx), { w, h } = P;
  deepPools(ctx, P, out);
  // the walls stand on the country's grass: what a fallen head leaves shows it
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!P.wall(x, y) || P.door(x, y)) continue;
    const g = grassAt(ctx, x, y);
    if (g) out.draw(g.piece, x * C, y * C, g.turn, true);
  }
  const pave = ruinGround(ctx, P, T, out);
  wetLine(ctx, P, T, out, pave);
}

/** Deep water has no square in it (docs/166 C2, the wetland ruins): one soft-sided oval round each run of deep squares, open water round it. */
function deepPools(ctx: Ctx, P: Plan, out: Canvas): void {
  const { w, h } = P;
  if (!ctx.has('deep') || !ctx.has('water')) return;
  const seen = new Uint8Array(w * h);
  for (let s = 0; s < w * h; s++) {
    if (seen[s] || ctx.sq.sq[s] !== 'deep') continue;
    const comp: number[] = [], todo = [s];
    seen[s] = 1;
    while (todo.length) {
      const i = todo.pop()!; comp.push(i);
      const x = i % w, y = Math.floor(i / w);
      for (const [a, b] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]] as const) {
        if (P.on(a, b) && !seen[b * w + a] && ctx.sq.sq[b * w + a] === 'deep') { seen[b * w + a] = 1; todo.push(b * w + a); }
      }
    }
    let mx = 0, my = 0, ax = -Infinity, ay = -Infinity;
    for (const i of comp) { const cx = (i % w) * C + 12, cy = Math.floor(i / w) * C + 12; mx += cx; my += cy; ax = Math.max(ax, cx); ay = Math.max(ay, cy); }
    mx /= comp.length; my /= comp.length;
    const rx = ax - mx + 15, ry = ay - my + 12;
    const x0 = Math.max(0, Math.floor((mx - rx) / C) - 1), x1 = Math.min(w - 1, Math.floor((mx + rx) / C) + 1);
    const y0 = Math.max(0, Math.floor((my - ry) / C) - 1), y1 = Math.min(h - 1, Math.floor((my + ry) / C) + 1);
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      if (!P.liquid(x, y)) continue;
      const deep = ctx.pick('deep', 'g:deep', x, y)!, water = ctx.pick('water', 'g:water', x, y)!;
      for (let v = 0; v < C; v++) for (let u = 0; u < C; u++) {
        const a = x * C + u, b = y * C + v;
        const inside = ((a - mx) / rx) ** 2 + ((b - my) / ry) ** 2 < 1 + 0.12 * Math.sin(a / 5) * Math.cos(b / 4);
        if (inside) out.copy(a, b, deep, u, v);
        else if (ctx.sq.at(x, y) === 'deep') out.copy(a, b, water, u, v);
      }
    }
  }
}

/** `ruin_dissolve`: whole stones go, each connected run of non-joint pixels kept or lost as one by `chance` at its middle; a joint goes unless it still touches a kept stone. */
function dissolve(ctx: Ctx, d: Int32Array, W: number, H: number, chance: (cx: number, cy: number) => number, joints: Set<number>, tint?: (cells: Int32Array, n: number, cx: number, cy: number) => void): void {
  const stone = hasher(ctx, 'stone'), joint = oneOf(joints), N = W * H;
  // 0 nothing, 1 a joint, 2 a stone's pixel, 3 a kept stone's
  const kind = new Uint8Array(N);
  for (let i = 0; i < N; i++) { const v = d[i]!; if ((v & AM) !== 0) kind[i] = joint(v) ? 1 : 2; }
  maskRuns(kind, W, H, 2, (cells, n) => {
    const [cx, cy] = middle(cells, n, W), f = cells[0]!;
    if (stone(f % W, Math.floor(f / W)) % 100 < chance(cx, cy)) {
      for (let k = 0; k < n; k++) kind[cells[k]!] = 3;
      tint?.(cells, n, cx, cy);
    }
  });
  for (let b = 0; b < H; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a, k0 = kind[i]!;
    if (k0 === 0 || k0 === 3) continue;
    if (k0 === 2) { d[i] = 0; continue; }
    let near = false;
    for (let j = -1; j <= 1 && !near; j++) for (let k = -1; k <= 1; k++) {
      const x = a + k, y = b + j;
      if (x >= 0 && y >= 0 && x < W && y < H && kind[y * W + x] === 3) { near = true; break; }
    }
    if (!near) d[i] = 0;
  }
}

/** Each pixel: 0 clear, 1 a joint (by `joint`), 2 any other drawn pixel. */
function kinds(d: Int32Array, joint?: (v: number) => boolean): Uint8Array {
  const k = new Uint8Array(d.length);
  for (let i = 0; i < d.length; i++) { const v = d[i]!; if ((v & AM) !== 0) k[i] = joint && joint(v) ? 1 : 2; }
  return k;
}

function near8(a: number, b: number, W: number, H: number, f: (j: number) => boolean): boolean {
  for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
    const x = a + i, y = b + j;
    if (x >= 0 && y >= 0 && x < W && y < H && f(y * W + x)) return true;
  }
  return false;
}

/**
 * `ruin_ground`: paving over the whole board that loses whole stones toward soft ground, moss going stone
 * by stone, joints moss or earth, and long grass growing up over the foot of a stone. Returns the paving
 * as laid (board px), for the wet line.
 */
function ruinGround(ctx: Ctx, P: Plan, T: Tones, out: Canvas): Int32Array {
  const { w, h } = P, W = w * C, H = h * C;
  const floors = ctx.all('floor'), patches = ctx.all('floor_patch');
  const layer = new Canvas(W, H), d = words(layer);
  // each square's chance a stone stays, by its distance to soft ground (on the board) and whether it's broken
  const odds = new Uint8Array(w * h);
  const within = (x: number, y: number, want: boolean, r2: number): boolean => {
    for (let j = -2; j <= 2; j++) for (let i = -2; i <= 2; i++) {
      if (i * i + j * j > r2 || !P.on(x + i, y + j)) continue;
      if (P.soft(x + i, y + j) === want) return true;
    }
    return false;
  };
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let c: number;
    if (ctx.sq.at(x, y) === 'void') c = 0;
    else if (P.soft(x, y)) c = P.wall(x, y + 1) || P.wall(x, y + 2) ? 0 : within(x, y, false, 1) ? 22 : 0;   // nothing strays out behind a wall
    else c = ctx.sq.at(x, y) === 'rough' ? 45 : within(x, y, true, 1) ? 72 : within(x, y, true, 4) ? 92 : 97;
    odds[y * w + x] = c;
  }
  // a square with no chance and none beside it lays nothing (a stone crossing into a live square still goes whole)
  const live = (x: number, y: number): boolean => {
    for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) if (P.on(x + i, y + j) && odds[(y + j) * w + x + i]) return true;
    return false;
  };
  const pf = hasher(ctx, 'f');
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (ctx.sq.at(x, y) === 'void' || !floors.length || !live(x, y)) continue;
    layer.draw(floors[pf(x, y) % floors.length]!, x * C, y * C, undefined, true);
  }
  // the first floor variants and the patch files are the same stones, the patch's gone olive
  const olive = new Map<number, number>();
  for (let k = 0; k < Math.min(floors.length, patches.length); k++) {
    const a = floors[k]!, b = patches[k]!;
    for (let j = 0; j < C; j++) for (let i = 0; i < C; i++) {
      const pa = at(a, i, j), pb = at(b, i, j);
      if (!T.joints.has(pa) && !T.joints.has(pb) && !olive.has(pa)) olive.set(pa, pb);
    }
  }
  const field = fieldOf(ctx), ms = hasher(ctx, 'ms');
  dissolve(ctx, d, W, H, (cx, cy) => odds[Math.floor(cy / C) * w + Math.floor(cx / C)]!, T.joints, (cells, n, cx, cy) => {
    const f = cells[0]!;
    if (ms(f % W, Math.floor(f / W)) % 100 < field(cx, cy) * 0.9 - 25) for (let k = 0; k < n; k++) { const i = cells[k]!; d[i] = olive.get(d[i]!) ?? d[i]!; }
  });
  if (ctx.set.biome === 'ashlands') fray(ctx, d, W, H, T.joints);
  const isJoint = oneOf(T.joints);
  const jn = hasher(ctx, 'jn'), jg = hasher(ctx, 'jg'), je = hasher(ctx, 'je');
  for (let b = 0; b < H; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a;
    if (!isJoint(d[i]!) || !((d[i]! & AM) !== 0)) continue;
    const g = field(a, b) + (jn(a >> 1, b >> 1) % 40) - 20;
    d[i] = g > 50 ? T.green[jg(a, b) % 3]! : T.earth[je(a, b) % 2]!;
  }
  const o = words(out);
  for (let i = 0; i < W * H; i++) if (((d[i]! & AM) !== 0)) o[i] = d[i]!;
  // long grass over the foot of a stone
  const sp = hasher(ctx, 'sp'), spt = hasher(ctx, 'spt');
  for (let b = 0; b < H - 1; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a;
    if (!((d[i]! & AM) !== 0) || ((d[i + W]! & AM) !== 0) || sp(a, b) % 3) continue;
    const t = 2 + (spt(a, b) % 4);
    for (let j = 0; j < t && b - j >= 0; j++) if (((d[i - j * W]! & AM) !== 0)) o[i - j * W] = T.blade[Math.min(2, Math.floor((j * 3) / t))]!;
  }
  if (ctx.set.biome === 'ashlands') ashDrifts(ctx, P, T, out, d);
  return d;
}

/** The ashlands' flags fray at their edge (its `ruins_board.py.txt` `fray`): square dark stones on pale ash read as UI squares, and whole-stone edges as a staircase. */
function fray(ctx: Ctx, d: Int32Array, W: number, H: number, joints: Set<number>): void {
  const jt = oneOf(joints), joint = { has: (v: number): boolean => ((v & AM) !== 0) && jt(v) };
  const owner = new Int32Array(W * H).fill(-1);
  const stones: Array<[Int32Array, number]> = [];
  maskRuns(kinds(d, (v) => joint.has(v)), W, H, 2, (cells, n) => { const c = cells.slice(0, n); for (const i of c) owner[i] = stones.length; stones.push([c, n]); });
  stones.forEach(([cells], k) => {
    // a stone with no other flag two pixels off goes
    const alone = !cells.some((i) => {
      const a = i % W, b = Math.floor(i / W);
      for (const [dx, dy] of [[2, 0], [-2, 0], [0, 2], [0, -2]] as const) {
        const x = a + dx, y = b + dy;
        if (x >= 0 && y >= 0 && x < W && y < H && owner[y * W + x] !== -1 && owner[y * W + x] !== k) return true;
      }
      return false;
    });
    if (alone) for (const i of cells) d[i] = 0;
  });
  for (let b = 0; b < H; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a;
    if (((d[i]! & AM) !== 0) && joint.has(d[i]!) && !near8(a, b, W, H, (j) => ((d[j]! & AM) !== 0) && !joint.has(d[j]!))) d[i] = 0;
  }
  maskRuns(kinds(d), W, H, 2, (cells, n) => { if (n < 260) for (let k = 0; k < n; k++) d[cells[k]!] = 0; });
  const edge: number[] = [];
  for (let b = 0; b < H; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a;
    if (!((d[i]! & AM) !== 0)) continue;
    if ((a + 1 < W && !((d[i + 1]! & AM) !== 0)) || (a > 0 && !((d[i - 1]! & AM) !== 0)) || (b + 1 < H && !((d[i + W]! & AM) !== 0)) || (b > 0 && !((d[i - W]! & AM) !== 0))) edge.push(i);
  }
  for (const i of edge) if (joint.has(d[i]!)) d[i] = 0;
  const bite = hasher(ctx, 'bite');
  for (const i of edge) {
    const a = i % W, b = Math.floor(i / W);
    if (bite(Math.floor(a / 3), Math.floor(b / 3)) % 3) continue;
    d[i] = 0;
    if (a + 1 < W) d[i + 1] = 0;
    if (b + 1 < H) d[i + W] = 0;
  }
}

/** Ash lying on the ashlands' flags: a slow field over stones and joints alike, in the colours `floor_patch` has that `floor` hasn't (its `ruins_board.py.txt`). */
function ashDrifts(ctx: Ctx, P: Plan, T: Tones, out: Canvas, pave: Int32Array): void {
  const inFloor = new Set<number>();
  for (const f of ctx.all('floor')) for (let y = 0; y < f.h; y++) for (let x = 0; x < f.w; x++) { const v = at(f, x, y); if (((v & AM) !== 0)) inFloor.add(v); }
  const ash = new Map<number, number>();
  for (const f of ctx.all('floor_patch')) for (let y = 0; y < f.h; y++) for (let x = 0; x < f.w; x++) {
    const v = at(f, x, y);
    if (((v & AM) !== 0) && !inFloor.has(v)) ash.set(v, f.px[(y * f.w + x) * 4]! + f.px[(y * f.w + x) * 4 + 1]! + f.px[(y * f.w + x) * 4 + 2]!);
  }
  const ashes = [...ash.entries()].sort((a, b) => a[1] - b[1]).map(([v]) => v);
  if (ashes.length < 4) return;
  const [J, D, B, Pa] = ashes as [number, number, number, number];
  const joints = new Set([...T.joints, ...T.green, ...T.earth]);
  const W = out.w, o = words(out), field = fieldOf(ctx), field2 = fieldOf(ctx), af = hasher(ctx, 'af'), am = hasher(ctx, 'am');
  // per square: water (no ash), or beside soft ground (more ash)
  const sqKind = new Uint8Array(P.w * P.h);
  for (let y = 0; y < P.h; y++) for (let x = 0; x < P.w; x++) {
    const near = [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([i2, j2]) => P.on(x + i2!, y + j2!) && P.soft(x + i2!, y + j2!));
    sqKind[y * P.w + x] = P.liquid(x, y) ? 2 : near ? 1 : 0;
  }
  for (let b = 0; b < out.h; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a, k0 = sqKind[Math.floor(b / C) * P.w + Math.floor(a / C)]!;
    if ((pave[i]! & AM) === 0 || k0 === 2) continue;
    const near = k0 === 1;
    const f = field(a, b, 44) * 0.62 + field2(a + 97, b + 31, 17) * 0.38 + (af(a >> 1, b >> 1) % 9) + (near ? 10 : 0);
    if (f <= 71) continue;
    const m = am(a, b) % 100;
    o[i] = joints.has(o[i]!) ? J : m < 10 ? D : m < 17 ? Pa : B;
  }
}

/**
 * The wet line (docs/166 C2, the wetland ruins): each water pixel the paving left bare, directly under or
 * beside a stone or the grass, draws `ruin_tones` column 20, with column 21 in the pixel below it; ice is
 * edged all round. No foam, no pale rim.
 */
function wetLine(ctx: Ctx, P: Plan, T: Tones, out: Canvas, pave: Int32Array): void {
  const { w, h } = P, W = w * C, H = h * C, o = words(out);
  const still = (a: number, b: number): boolean => {
    if (a < 0 || b < 0 || a >= W || b >= H) return true;   // the board's edge is no bank
    return P.liquid(Math.floor(a / C), Math.floor(b / C)) && !((pave[b * W + a]! & AM) !== 0);
  };
  const winter = ctx.set.season === 'winter', ash = ctx.set.biome === 'ashlands';
  const dark = new Uint8Array(W * H);
  const sh = hasher(ctx, 'sh'), sh2 = hasher(ctx, 'sh2');
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!P.liquid(x, y)) continue;
    for (let b = y * C; b < y * C + C; b++) for (let a = x * C; a < x * C + C; a++) {
      if (!still(a, b)) continue;
      // the ashlands' spring: a rust stain on the bank above it
      if (ash && b > 2 && !P.liquid(x, Math.floor((b - 1) / C)) && sh(a >> 1, b) % 3) {
        o[(b - 2) * W + a] = T.stain[1];
        if (sh2(Math.floor(a / 3), b) % 2) o[(b - 3) * W + a] = T.stain[0];
      }
      if (!still(a, b - 1) || !still(a - 1, b) || !still(a + 1, b) || (winter && !still(a, b + 1))) { o[b * W + a] = T.wet; dark[b * W + a] = 1; }
    }
  }
  for (let b = 1; b < H; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a;
    if (!dark[i] && dark[i - W] && still(a, b)) o[i] = T.wetSoft;
  }
}

/** `raised` (walls.ts): a door set in a front, raised with its wall, by its whole box (`set_in_box`) or its own pixels. */
function raised(tile: Piece, base: Piece, front: Piece, box: boolean): Piece {
  const px = front.px.slice(), dy = front.h - C;
  const diff: Array<[number, number]> = [];
  for (let y = 0; y < C; y++) for (let x = 0; x < C; x++) if (at(tile, x, y) !== at(base, x, y)) diff.push([x, y]);
  let cells = diff;
  if (box && diff.length) {
    const x0 = Math.min(...diff.map((q) => q[0])), x1 = Math.max(...diff.map((q) => q[0])), y0 = Math.min(...diff.map((q) => q[1]));
    cells = [];
    for (let y = y0; y < C; y++) for (let x = x0; x <= x1; x++) cells.push([x, y]);
  }
  for (const [x, y] of cells) px.set(tile.px.subarray((y * tile.w + x) * 4, (y * tile.w + x) * 4 + 4), ((y + dy) * C + x) * 4);
  return { ...front, px };
}

const COURSE_JOINT = [6, 0, 6, 0, 6];

/**
 * `ruin_walls`: the walls drawn on one layer the board's size (raised `wall_rise`), then cut into one
 * standing image per wall square (each pixel to the square whose raised box holds it), so they sort with
 * the units as `paintWalls`' do. What falls on no wall square (a head's line over open ground, a weed)
 * lies on the ground.
 */
export function ruinWalls(ctx: Ctx, ground: Canvas): WallsOut {
  const P = planOf(ctx), T = tonesOf(ctx), R = ruinOf(ctx), { w, h } = P, look = ctx.look;
  const rise = look.wall_rise ?? 12;
  const W = w * C, H = h * C + rise;   // the layer: board px, `rise` rows down (a head at y * C - rise is at y * C here)
  const out: WallsOut = { standing: [], lights: [], headroom: rise };
  const L = new Canvas(W, H), d = words(L);

  // the heads: every wall's top, losing whole stones toward open ground
  const head = new Canvas(W, H), hd = words(head), tops = ctx.all('wall_top');
  const pt = hasher(ctx, 't');
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (P.wall(x, y) && tops.length) head.draw(tops[pt(x, y) % tops.length]!, x * C, y * C, undefined, true);
  const hj = oneOf(T.headJoints), headJoint = { has: (v: number): boolean => ((v & AM) !== 0) && hj(v) };
  dissolve(ctx, hd, W, H, (a, b) => {
    const x = Math.floor(a / C), y = Math.floor(b / C);
    if (P.deep(x, y)) return 10;
    const lx = a % C, ly = b % C;
    const dd = Math.min(!P.wall(x - 1, y) ? lx : 99, !P.wall(x + 1, y) ? C - lx : 99, !P.wall(x, y - 1) ? ly : 99);
    return dd < 4 ? 45 : dd < 7 ? 70 : dd < 10 ? 92 : 99;
  }, T.headJoints);
  if (ctx.set.biome === 'ashlands') {
    // a wide wall's middle goes, stone by stone: a large pale mass read as paving, not as a wall standing
    const mid = hasher(ctx, 'mid');
    maskRuns(kinds(hd, (v) => headJoint.has(v)), W, H, 2, (cells, n) => {
      const [cx, cy] = middle(cells, n, W), x = Math.floor(cx / C), y = Math.floor(cy / C);
      const around = [[1, 0], [-1, 0], [0, 1], [0, -1]].filter(([i, j]) => P.wall(x + i!, y + j!)).length;
      if (P.wall(x, y) && around >= 3 && mid(Math.floor(cx / 12), Math.floor(cy / 12)) % 100 < 86) for (let k = 0; k < n; k++) hd[cells[k]!] = 0;
    });
    for (let b = 0; b < H; b++) for (let a = 0; a < W; a++) {
      const i = b * W + a;
      if (((hd[i]! & AM) !== 0) && headJoint.has(hd[i]!) && !near8(a, b, W, H, (j) => ((hd[j]! & AM) !== 0) && !headJoint.has(hd[j]!))) hd[i] = 0;
    }
  }
  if (LONE_STUB.has(ctx.set.biome)) {
    // a head stone left alone on open ground reads as a blank button: none is laid whole; one in two leaves low rubble (docs/166 C2, the desert ruins)
    const rubble = [...ctx.all('decal_rubble'), ...ctx.all('decal_stones')], lone = hasher(ctx, 'lone'), lonep = hasher(ctx, 'lonep');
    maskRuns(kinds(hd), W, H, 2, (cells, n) => {
      const [cx, cy] = middle(cells, n, W), x = Math.floor(cx / C), y = Math.floor(cy / C);
      if (n > 400 || (P.on(x, y) && P.on(x, y + 1) && !P.wall(x, y + 1))) return;   // one a front stands under stays
      for (let k = 0; k < n; k++) hd[cells[k]!] = 0;
      if (rubble.length && lone(cx, cy) % 2 === 0) ground.draw(rubble[lonep(cx, cy) % rubble.length]!, Math.min(cx - 12, W - C), Math.min(cy - rise - 12, h * C - C));
    });
  }
  // the short south face under every stone that overhangs a gap, then the heads over it, then a line round both
  const solid = new Uint8Array(W * H);
  for (let b = 0; b < H - 4; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a;
    if (!((hd[i]! & AM) !== 0) || ((hd[i + W]! & AM) !== 0)) continue;
    for (let j = 0; j < T.face.length; j++) if (!((hd[i + (1 + j) * W]! & AM) !== 0)) { d[i + (1 + j) * W] = T.face[j]!; solid[i + (1 + j) * W] = 1; }
  }
  for (let i = 0; i < W * H; i++) if (((hd[i]! & AM) !== 0)) { d[i] = hd[i]!; solid[i] = 1; }
  for (let b = 0; b < H; b++) for (let a = 0; a < W; a++) {
    const i = b * W + a;
    if (solid[i]) continue;
    if ((a + 1 < W && solid[i + 1]) || (a > 0 && solid[i - 1]) || (b + 1 < H && solid[i + W]) || (b > 0 && solid[i - W])) d[i] = T.headLine;
  }
  // moss on the heads: soft irregular drifts, lit from the upper left, about one square in two
  const hm = hasher(ctx, 'hm'), hmx = hasher(ctx, 'hmx'), hmy = hasher(ctx, 'hmy');
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!P.wall(x, y) || P.deep(x, y) || hm(x, y) % 2) continue;
    const cx = x * C + 4 + (hmx(x, y) % 16), cy = y * C + 4 + (hmy(x, y) % 16);
    const lumps = [0, 1, 2].map((n) => [cx + (ctx.roll(`lx${n}`, x, y) % 9) - 4, cy + (ctx.roll(`ly${n}`, x, y) % 7) - 3, 1 + (ctx.roll(`lr${n}`, x, y) % 3)] as const);
    const blot = new Set<number>();
    for (const [lx, ly, r] of lumps) for (let b = ly - r; b <= ly + r; b++) for (let a = lx - r; a <= lx + r; a++) {
      if (a < 0 || b < 0 || a >= W || b >= H) continue;
      if ((a - lx) ** 2 + (b - ly) ** 2 <= r * r + 1 && solid[b * W + a]) blot.add(b * W + a);
    }
    for (const i of blot) {
      const up = blot.has(i - W), left = blot.has(i - 1), down = blot.has(i + W);
      d[i] = !up && !left ? T.moss[3]! : !up ? T.moss[2]! : !down ? T.moss[0]! : T.moss[1]!;
    }
  }

  // the fronts: whole blocks lost from the top, the skyline stepping along its own courses and falling away toward each end of a run
  const faces = ctx.all('wall_face'), ivy = ctx.all('wall_face_ivy'), blank = faces[0];
  const sky = hasher(ctx, 'sky'), fa = hasher(ctx, 'fa'), ivyR = hasher(ctx, 'ivy'), iv = hasher(ctx, 'iv');
  const mossR = hasher(ctx, 'moss'), msR = hasher(ctx, 'ms'), mc = hasher(ctx, 'mc'), dr = hasher(ctx, 'dr'), dl = hasher(ctx, 'dl'), dc = hasher(ctx, 'dc');
  const blocks = [0, 1, 2, 3, 4].map((c) => hasher(ctx, `b${c}`));
  const top = new Int32Array(W + 2);   // per board column (+1): the first row of the raised front that stands
  for (let y = 0; y < h && blank; y++) {
    let any = false;
    for (let x = 0; x < w; x++) if (P.front(x, y)) any = true;
    if (!any) continue;
    // each front's run, from its first square to past its last
    const runA = new Int32Array(w), runB = new Int32Array(w);
    for (let x = 0; x < w; x++) {
      if (!P.front(x, y)) continue;
      runA[x] = P.front(x - 1, y) ? runA[x - 1]! : x;
      let b = x;
      while (P.front(b + 1, y)) b++;
      runB[x] = b;
    }
    const runEnd = (gx: number): number => {
      const x = Math.floor(gx / C);
      if (!P.front(x, y)) return 0;
      return Math.min(gx - runA[x]! * C, (runB[x]! + 1) * C - gx);
    };
    const lv = (i: number): number => R.levels[sky(i, y) % R.levels.length]!;
    const skyLevel = (gx: number): number => {
      const v = Math.floor(gx / C), t = (gx % C) / C;
      return Math.max(lv(v) * (1 - t) + lv(v + 1) * t, R.end_fall - runEnd(gx) / 8);
    };
    const kept = (course: number, gx: number): boolean => {
      const bx = gx - ((((gx - COURSE_JOINT[course]!) % 12) + 12) % 12);
      return course >= skyLevel(bx + 6) + (blocks[course]!(bx, y) % 100) / 100 - 0.5;
    };
    top.fill(1 << 20);
    for (let gx = 0; gx < W; gx++) {
      const x = Math.floor(gx / C);
      if (!P.front(x, y)) continue;
      // solid from row r when every course from r / 6 down stands; always from row 30, and from `door_rows` in a doorway
      let r0 = 30;
      for (let c = 4; c >= 0 && kept(c, gx); c--) r0 = c * 6;
      top[gx + 1] = Math.min(r0, P.door(x, y) ? R.door_rows : 30);
    }
    const T0 = (gx: number): number => (gx < 0 || gx >= W ? 1 << 20 : top[gx + 1]!);
    const S = (gx: number, r: number): boolean => r >= 0 && r >= T0(gx);
    const has = (gx: number, r: number): boolean => r >= T0(gx) - 2;
    for (let x = 0; x < w; x++) {
      if (!P.front(x, y)) continue;
      const X = x * C, Y = y * C;   // layer px of the raised front's top
      let f = tallFront(P.door(x, y) ? blank : faces[fa(x, y) % faces.length]!, look);
      if (P.door(x, y)) {
        const s = ctx.sq.at(x, y), t = ctx.sq.tile(x, y);
        const open = ctx.all('door_open_front');
        const ds = s === 'way' ? ctx.all('hidden_way_front') : t?.doorOpen && open.length ? open : ctx.all('door_closed_front');
        if (ds.length) f = raised(ds[ctx.roll('door', x, y) % ds.length]!, blank, f, !!look.set_in_box);
      }
      const fw = new Int32Array(f.px.slice().buffer);
      const rows = f.h;
      for (let a = 0; a < C; a++) {
        const gx = X + a;
        for (let r = 0; r < rows && Y + r < H; r++) {
          const i = (Y + r) * W + gx;
          if (S(gx, r)) { const v = fw[r * C + a]!; if (((v & AM) !== 0)) d[i] = v; }
          else if (S(gx, r + 1)) d[i] = T.capBody;   // a block's top, seen from a little above
          else if (S(gx, r + 2)) d[i] = T.capLit;
          else if (has(gx - 1, r) || has(gx + 1, r) || has(gx, r + 1)) d[i] = T.line;   // the silhouette
        }
      }
      // moss cushions on the exposed ledges, and a drip down from one now and then
      for (let a = 0; a < C; a++) {
        const gx = X + a, r = T0(gx);
        if (r >= rows) continue;
        const m = mossR(Math.floor(gx / 6), y * 64 + r);
        if (m % 100 >= 55) continue;
        for (let j = 0; j < 2 + (m % 2); j++) {
          const rr = r - 1 - j + (msR(gx, y) % 2);
          if (rr >= 0) d[(Y + rr) * W + gx] = T.moss[Math.min(j + 1 + (mc(gx, y) % 2), 3)]!;
        }
        if (dr(gx, y) % 8 === 0) for (let j = 0; j < 1 + (dl(gx, y) % 5); j++) if (Y + r + j < H) d[(Y + r + j) * W + gx] = T.moss[dc(gx, j) % 2]!;
      }
      // ivy hangs from where the wall still stands at its middle (any sheet hung on a front: the highland's harl is not a plant)
      if (ivy.length && !P.door(x, y) && ivyR(x, y) % R.ivy_every === 0) {
        const r0 = T0(X + 12) < rows ? T0(X + 12) : 30, p = ivy[iv(x, y) % ivy.length]!, hgt = Math.max(1, 33 - r0);
        for (let v = 0; v < Math.min(hgt, p.h); v++) for (let u = 0; u < Math.min(C, p.w); u++) {
          const c = at(p, u, v), yy = Y + r0 - 2 + v;
          if (((c & AM) !== 0) && yy >= 0 && yy < H) d[yy * W + X + u] = c;
        }
      }
    }
  }

  // brush and trees on the ground a wall went back to (no tree on a square a front's head rises over)
  const trees = ctx.all('stand_tree'), brush = ctx.all('thicket'), dp = hasher(ctx, 'dp');
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!P.deep(x, y) || (!trees.length && !brush.length)) continue;
    const j = dp(x, y) % 12;
    const p = j < 2 && trees.length ? trees[j % trees.length] : j < 6 && brush.length ? brush[j % brush.length] : undefined;
    if (!p) continue;
    const px = x * C + C / 2 - Math.floor(p.w / 2), py = y * C + C - p.h;
    out.standing.push({ kind: p.piece === 'thicket' ? 'thicket' : 'stand', at: { x, y }, x: px, y: py, w: p.w, h: p.h, foot: y * C + C / 2, frame: p.frame });
    out.headroom = Math.max(out.headroom, -py);
  }
  // weeds along a front's foot and over what went back to the country
  const tuft = T.tuft, gw = words(ground);
  const blade = (gx: number, gy: number, key: string, tall: number, onGround: boolean): void => {
    const put = (a: number, b: number, c: number): void => {
      if (a < 0 || a >= W) return;
      if (onGround) { if (b >= 0 && b < h * C) gw[b * W + a] = c; }
      else if (b + rise >= 0 && b + rise < H) d[(b + rise) * W + a] = c;
    };
    [0, 1, -1, 2].forEach((dx, i) => {
      const t = tall - Math.floor(i / 2) - (ctx.roll(`${key}t${i}`, gx, gy) % 2);
      for (let j = 0; j < Math.max(t, 1); j++) put(gx + dx, gy - j, tuft[Math.min(1 + j + (ctx.roll(`${key}${i}`, gx, gy) % 2), 4)]!);
    });
    put(gx, gy + 1, tuft[0]!);
  };
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (P.deep(x, y) && ctx.roll('dt', x, y) % 2 === 0) blade(x * C + 4 + (ctx.roll('dtx', x, y) % 16), y * C + 5 + (ctx.roll('dty', x, y) % 14), 'd', 3, true);
    if (P.front(x, y) && y + 1 < h) {
      for (let n = 0; n < 3; n++) if (ctx.roll(`ft${n}`, x, y) % 3 === 0) blade(x * C + 3 + (ctx.roll(`ftx${n}`, x, y) % 18), (y + 1) * C - 1 + (ctx.roll(`fty${n}`, x, y) % 3), `f${n}`, 3, false);
    }
  }
  if (ctx.set.biome === 'ashlands') traceHeads(d, hd, W, H, T);

  // the soft shadow a wall throws on the square below it, and a few chips of fallen stone there
  const chips = [...ctx.all('decal_rubble').slice(0, 1), ...ctx.all('decal_fallen_stones')];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (P.wall(x, y) || ctx.sq.at(x, y) === 'void') continue;
    if (P.wall(x, y - 1)) ground.rect(x * C, y * C, x * C + C - 1, y * C + 4, [0, 0, 0, 70]);
    const t = ctx.sq.tile(x, y);
    if (!chips.length || P.liquid(x, y) || t?.exit || t?.objective || t?.hazard) continue;
    const s = ctx.sq.at(x, y);
    if ((s === 'floor' && P.wall(x, y - 1) && ctx.roll('ch', x, y) % 4 === 0) || (s === 'rough' && ctx.roll('ch2', x, y) % 8 === 0)) {
      ground.draw(chips[ctx.roll('chp', x, y) % chips.length]!, x * C, y * C, ctx.roll('chf', x, y) % 2 ? { flipX: true } : undefined);
    }
  }

  // cut the layer: each pixel to the wall square whose raised box holds it, the rest onto the ground
  const cells = new Map<number, [Canvas, Int32Array]>();
  const g = words(ground);
  for (let b = 0; b < H; b++) for (let a = 0; a < W; a++) {
    const v = d[b * W + a]!;
    if (!((v & AM) !== 0)) continue;
    const x = Math.floor(a / C);
    let y = Math.floor(b / C);
    if (!P.wall(x, y)) y = P.front(x, y - 1) && b - rise < y * C ? y - 1 : -1;
    if (y >= 0 && P.deep(x, y) && b < rise) continue;   // a fallen head's last stones never float over the board's top
    if (y < 0) { if (b - rise >= 0 && b - rise < h * C) g[(b - rise) * W + a] = v; continue; }
    let c = cells.get(y * w + x);
    if (!c) { const cv = new Canvas(C, C + rise); c = [cv, words(cv)]; cells.set(y * w + x, c); }
    c[1][(b - y * C) * C + (a - x * C)] = v;
  }
  for (const [i, [c]] of cells) {
    const x = i % w, y = Math.floor(i / w);
    out.standing.push({ kind: 'wall', at: { x, y }, x: x * C, y: y * C - rise, w: C, h: C + rise, foot: y * C + C / 2, px: c.px } satisfies Standing);
  }
  return out;
}

/** The ashlands' head line (its `ruins_board.py.txt`): a continuous line round every head, traced from what is drawn; a dash touching nothing goes. */
function traceHeads(d: Int32Array, hd: Int32Array, W: number, H: number, T: Tones): void {
  const lines = new Set([T.line, T.headLine]), tufts = new Set(T.tuft), moss = new Set(T.moss), face = new Set(T.face);
  const drawn = new Uint8Array(W * H), vis = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) {
    const v = d[i]!;
    if (!((v & AM) !== 0) || lines.has(v) || tufts.has(v)) continue;
    drawn[i] = 1;
    if ((((hd[i]! & AM) !== 0) && v === hd[i]) || moss.has(v) || face.has(v)) vis[i] = 1;
  }
  const n4 = (i: number, f: (j: number) => void): void => {
    const a = i % W;
    if (a + 1 < W) f(i + 1);
    if (a > 0) f(i - 1);
    if (i + W < W * H) f(i + W);
    if (i >= W) f(i - W);
  };
  for (let i = 0; i < W * H; i++) {
    if (!lines.has(d[i]!)) continue;
    let touches = false;
    n4(i, (j) => { if (drawn[j]) touches = true; });
    if (!touches) d[i] = 0;
  }
  for (let i = 0; i < W * H; i++) {
    if (!vis[i]) continue;
    n4(i, (j) => {
      if (vis[j]) return;
      if (drawn[j]) { if (j === i + W) d[j] = T.headLine; }   // along the head's foot, where a front begins
      else d[j] = T.line;
    });
  }
}
